import React, { useEffect } from "react";
import {
  SectionHeaderTitle,
  SectionTitleTypography,
  SessionSelectionWidgetContainer,
  SectionSubTitleTypography,
  SelectedSessionContainer,
  UnSelectedSessionContainer,
  SelectedSessionLabel,
  SelectedSessionName,
  SelectedSessionYear,
  SelectedSessionDay,
  LabelTypography,
  DateContainer,
  InputSection,
  TrashButton,
  RegisterIcon,
  RegisterSuccessTitle,
  RegisterSuccessText
} from "./SessionSelectionWidget.styles";
import { Button, TextField, InfoMessage, Checkbox } from "..";
import { SingleSession } from "../../models/Session";
import { ForgottenPasswordLink, StyledLink } from "../Register/BasicInfo/BasicInfo.styles";
import { Icon } from "../../icons";
import { ButtonSwitch } from "../ButtonSwitch/ButtonSwitch";
import { Box, Stack, useMediaQuery } from "@mui/material";
import { UserInterface } from "../../models/User";
import { emailValidation } from "../../utils/regex";
import { isLoggedIn } from "../../utils/auth";
import dayjs from "dayjs";
import { utcToVenueLocal } from "../../utils/dates";
import { LoginInterface } from "../../models/Login";
import { registerBasicInfo } from "../../services/AuthenticationService";
import { RegisterBasicInfoInterface } from "../../models/Register";
import { useAuthContext } from "src/contexts/AuthContext";
import { handleAutoScrolling } from "src/utils/bookingInterestUtils";
import { isSessionInLastMinPhase } from "src/utils/sessionUtils";
import { extractErrorMessageFromProblemDetails } from "src/utils/string-utils";

export interface CustomSessionSelectionWidgetProps {
  showName?: string | null;
  showMinimumAge?: number | null;
  selectedSession?: SingleSession | null;
  setSeletedSession: any;
  setSelectedSessionId: any;
  onNextButtonClick: Function;
  onRegisterSuccess?: (email: string, firstName: string) => void;
  user?: UserInterface | null;
}

export const SessionSelectionWidget = ({
  showName = null,
  selectedSession,
  setSeletedSession,
  setSelectedSessionId,
  showMinimumAge,
  onNextButtonClick,
  onRegisterSuccess,
  user,
  ...rest
}: CustomSessionSelectionWidgetProps) => {
  const [isTTTMember, setIsTTTMember] = React.useState<boolean | undefined>(undefined);
  const [isTTTAgreed, setIsTTTAgreed] = React.useState<boolean>(false);
  const [email, setEmail] = React.useState<string>('');
  const [firstName, setFirstName] = React.useState<string>('');
  const [lastName, setLastName] = React.useState<string>('');
  const [emailError, setEmailError] = React.useState<boolean>(false);
  const [firstNameError, setFirstNameError] = React.useState<boolean>(false);
  const [lastNameError, setLastNameError] = React.useState<boolean>(false);
  const [registerError, setRegisterError] = React.useState<boolean>(false);
  const [registerSucceded, setRegisterSucceded] = React.useState<boolean>(true);
  const [loginError, setLoginError] = React.useState<boolean>(false);
  const [errorMessage, setErrorMessage] = React.useState<string>('');
  const [password, setPassword] = React.useState<string>('');
  const [loginInProgress, setLoginInProgress] = React.useState<boolean>(false);
  const [applyButtonDisabled, setApplyButtonDisabled] = React.useState<boolean>(true);
  const [loading, setLoading] = React.useState<boolean>(false);
  const isMobileScreen = useMediaQuery('(max-width:600px)');

  // Consolidated flow (TTT-688 §12): once the last-min phase has started, new applicants
  // go straight onto the last minute list, so the CTA says so.
  const isLastMinPhase = isSessionInLastMinPhase(selectedSession);
  const applyButtonLabel = isLastMinPhase ? "Apply for Last Min Tickets" : "Apply for Tickets";

  const { signIn } = useAuthContext();

  useEffect(() => {
    setIsTTTMember(isLoggedIn() ? true : undefined);
  }, []);

  useEffect(() => {
    setEmail('');
    setPassword('');
    setFirstName('');
    setLastName('');
    setEmailError(false);
    setFirstNameError(false);
    setLastNameError(false);
    setIsTTTAgreed(false);

    if(selectedSession && isTTTMember === true) {
      setApplyButtonDisabled(false);
    }
    if(isTTTMember === false) {
      if(selectedSession && isTTTAgreed) {
        setApplyButtonDisabled(false);
      } else {
        setApplyButtonDisabled(true);
      }
    }
  }, [isTTTMember]);

  useEffect(() => {
    if(isTTTMember === false) {
      if(isTTTAgreed && selectedSession && email && firstName && lastName) {
        setApplyButtonDisabled(false);
      } else {
        setApplyButtonDisabled(true);
      }
    } else if(isTTTMember === true) {
      if(selectedSession) {
        setApplyButtonDisabled(false);
      } else {
        setApplyButtonDisabled(true);
      }
    }
  }, [isTTTAgreed, email, firstName, lastName]);

  useEffect(() => {
    setIsTTTAgreed(false);
    setRegisterSucceded(false);

    //apply for tickets button validation
    if(selectedSession) {
      if(isTTTMember === true) {
        //user was already logged in
        setApplyButtonDisabled(false);
      }
    } else {
      setApplyButtonDisabled(true);
    }
  }, [selectedSession]);

  const handleRemoveSession = () => {
    setSeletedSession(null);
    setSelectedSessionId(undefined);
  }

  const handleLogin = async () => {
    let loginData: LoginInterface = { email: "" };

    if (!email) {
      setEmailError(true);
      setLoginError(true);
      setErrorMessage("Oops! Your email is missing.");
      return;
    }
    if (!emailValidation(email)) {
      setEmailError(true);
      setLoginError(true);
      setErrorMessage("Oops! Your email appears to be in the wrong format.");
      return;
    }
    if(!password) {
      setLoginError(true);
      setErrorMessage("Oops! Your password is missing.");
      return;
    }

    setLoginInProgress(true);
    setLoading(true);
    loginData = { email: email, password: password };
    signIn(loginData).then(async (response) => {
      if (response.data) {
        onNextButtonClick(selectedSession?.id);
        setLoading(false);
      } else {
        if (typeof response.error === "string") {
          setErrorMessage(response.error);
        } else {
          setErrorMessage(extractErrorMessageFromProblemDetails(response.error!));
        }
        setLoginError(true);
        setLoginInProgress(false);
        setLoading(false);
      }
    });
  };

  const handleRegister = async () => {
    let registerData: RegisterBasicInfoInterface = {
      user: {
        email: email,
        firstName: firstName,
        lastName: lastName,
      }
    };

    setLoading(true);
    registerBasicInfo(registerData).then((res) => {
      if (res.status == 400) {
        setRegisterError(true);
        setErrorMessage(res.errors?.message[0]);
        setLoading(false);
      } else if (res.status == 200) {
        setLoading(false);
        if (onRegisterSuccess) {
          onRegisterSuccess(email, firstName);
        } else {
          setRegisterSucceded(true);
        }
      }
    });
  };

  const handleApplyForTickets = () => {
    if(!isLoggedIn()) {
      if(isTTTMember === true) {
        handleLogin();
      } else if(isTTTMember === false) {
        if(isTTTAgreed) {
          if (!emailValidation(email)) {
            setEmailError(true);
            setLoginError(true);
            setErrorMessage("Oops! Your email appears to be in the wrong format.");
            return;
          }

          if(!firstName) {
            setErrorMessage("Oops! Your first name is missing.");
            setFirstNameError(true);
            return;
          }
          if(!lastName) {
            setErrorMessage("Oops! Your last name is missing.");
            setLastNameError(true);
            return;
          }

          handleRegister();
        }
      }
    } else {
      onNextButtonClick(selectedSession?.id);
    }
  }

  return (
    <SessionSelectionWidgetContainer>
      <SectionHeaderTitle>
        <SectionSubTitleTypography>{showName}</SectionSubTitleTypography>
        <SectionTitleTypography>Your Application:</SectionTitleTypography>
      </SectionHeaderTitle>

        {selectedSession && (
          <SelectedSessionContainer>
            <Stack
              direction="column"
              justifyContent="space-between"
            >
              <SelectedSessionLabel>Session:</SelectedSessionLabel>
              <SelectedSessionName>{selectedSession.name}</SelectedSessionName>
            </Stack>

            <Stack
              direction="row"
              alignItems='center'
            >
              <DateContainer>
                <SelectedSessionDay>{(utcToVenueLocal(selectedSession.showStartTime, selectedSession.shootingLocation?.timeZone) ?? dayjs(selectedSession.showStartTime)).format('dd')}</SelectedSessionDay>
                <SelectedSessionYear>{(utcToVenueLocal(selectedSession.showStartTime, selectedSession.shootingLocation?.timeZone) ?? dayjs(selectedSession.showStartTime)).format('DD MMM')}</SelectedSessionYear>
              </DateContainer>

              <TrashButton onClick={() => handleRemoveSession()}>
                <Icon icon="Trash"  />
              </TrashButton>
            </Stack>
          </SelectedSessionContainer>
        )}

        {!selectedSession && (
          <UnSelectedSessionContainer>
            <Button
              gradientStyle="gradient2"
              onClick={() => handleAutoScrolling("session-selection")}
              size="md"
              variant="primary"
              leftIcon={<Icon icon="ArrowLeft" />}
            >
              Select a Session
            </Button>
          </UnSelectedSessionContainer>
        )}

      {((user === null || user?.loginStatus === "logged out") && !registerSucceded) && (
        <>
          <LabelTypography>
            Are you already a TTT Member?
          </LabelTypography>

          <ButtonSwitch
            leftButtonChildren="Yes"
            rightButtonChildren="No"
            active={isTTTMember ? "left" : (isTTTMember === undefined ? undefined : "right")}
            onLeftButtonClick={() => setIsTTTMember(true)}
            onRightButtonClick={() => setIsTTTMember(false)}
            leftButtonDataCy="member-status-yes"
            rightButtonDataCy="member-status-no"
            sx={{width: '100%'}}
          />

          <InputSection>
            {isTTTMember === false && (
              <>
                <TextField
                  variant="secondary"
                  placeholder="Email"
                  size="lg"
                  value={email}
                  error={emailError}
                  disabled={loginInProgress}
                  fullWidth
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setEmail(event.target.value);
                    if (emailError) {
                      if (!emailValidation(event.target.value)) {
                        setEmailError(true);
                      } else {
                        setEmailError(false);
                        setLoginError(false);
                      }
                    }
                  }}
                  InputProps={{ spellCheck: "false", autoCapitalize: "none" }}
                />
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  gap={1.5}
                  marginTop={1.5}
                  marginBottom={1}
                >
                  <TextField
                    value={firstName}
                    error={firstNameError}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setFirstName(event.target.value);
                      if (firstName) {
                        setFirstNameError(false);
                      }
                    }}
                    placeholder="First Name"
                    size="lg"
                    fullWidth
                    variant="secondary"
                  />
                  <TextField
                    value={lastName}
                    error={lastNameError}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setLastName(event.target.value);
                      if (firstName) {
                        setLastNameError(false);
                      }
                    }}
                    placeholder="Last Name"
                    size="lg"
                    fullWidth
                    variant="secondary"
                  />
                </Stack>

                {(emailError || firstNameError || lastNameError || registerError) && (
                  <InfoMessage
                    variant="error"
                    hasBackground={false}
                    message={errorMessage}
                  />
                )}

                {(firstName || lastName) && (
                  <InfoMessage
                    variant="info"
                    hasBackground={false}
                    message={"Please enter your real name as this will appear on your ticket"}
                  />
                )}
              </>
            )}

            {isTTTMember === true && (
              <>
                <TextField
                  variant="secondary"
                  placeholder="Email"
                  size="lg"
                  value={email}
                  error={emailError}
                  disabled={loginInProgress}
                  fullWidth
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setEmail(event.target.value);
                    if (emailError) {
                      if (!emailValidation(event.target.value)) {
                        setEmailError(true);
                      } else {
                        setEmailError(false);
                        setLoginError(false);
                      }
                    }
                  }}
                  InputProps={{ spellCheck: "false", autoCapitalize: "none" }}
                />
                <Box marginTop={1.5} marginBottom={1}>
                  <TextField
                    variant="secondary"
                    type="password"
                    size="lg"
                    placeholder="Password"
                    value={password}
                    error={loginError}
                    disabled={loginInProgress}
                    fullWidth
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setPassword(event.target.value);
                    }}
                    InputProps={{ spellCheck: "false", autoCapitalize: "none" }}
                  />
                </Box>

                <ForgottenPasswordLink>
                  <StyledLink href="/reset-password">
                    Forgotten Password
                  </StyledLink>
                </ForgottenPasswordLink>

                {(emailError || loginError) && (
                  <InfoMessage
                    variant="error"
                    hasBackground={false}
                    message={errorMessage}
                  />
                )}
              </>
            )}

            {isTTTMember === false && (
              <Checkbox
                sx={{border: '.1rem solid'}}
                checked={isTTTAgreed}
                onChange={setIsTTTAgreed}
                label={
                  <>
                    I agree to TTT’s {" "}
                    <StyledLink
                      target="_blank"
                      href="https://www.thatstheticket.com.au/_files/ugd/e9ec41_6389a51e79cb4768a2027f293c0b6dd0.pdf"
                    >
                      Privacy Policy and Collection Statement.
                    </StyledLink>
                    &nbsp;
                    <br/>*Fear not! We don't sell or share your contact details with anyone.
                  </>
                }
              />
            )}
          </InputSection>
        </>
      )}

      {(!registerSucceded && !isMobileScreen) && (
        <Button
          disabled={applyButtonDisabled}
          gradientStyle="gradient1"
          onClick={() => handleApplyForTickets()}
          size="md"
          variant="primary"
          sx={{width: '100%', marginTop: '1.5rem'}}
          loading={loading}
          data-cy="button-booking-next"
        >
          {applyButtonLabel}
        </Button>
      )}

      {(isMobileScreen && !applyButtonDisabled) && (
        <Button
          disabled={applyButtonDisabled}
          gradientStyle="gradient1"
          onClick={() => handleApplyForTickets()}
          size="md"
          variant="primary"
          sx={{width: '100%', marginTop: '1.5rem'}}
          loading={loading}
          data-cy="button-booking-next"
        >
          {applyButtonLabel}
        </Button>
      )}

      {registerSucceded && (
        <>
          <RegisterIcon>
            <Icon icon="PaperAirplane" size="lg" />
          </RegisterIcon>
          <RegisterSuccessTitle>Verify Your Email</RegisterSuccessTitle>
          <RegisterSuccessText>We have just sent a confirmation email to {email}.</RegisterSuccessText>
        </>
      )}
    </SessionSelectionWidgetContainer>
  );
};

export interface SessionSelectionWidgetProps
  extends React.ComponentProps<typeof SessionSelectionWidget> {
  as?: React.ElementType;
}

SessionSelectionWidget.displayName = "SessionSelectionWidget";
